import { useState, useEffect, useRef, useCallback } from "react";
import { loadSpotifySDK } from "@/lib/spotify/sdk-loader";
import { getCurrentUserProfile } from "@/lib/spotify/api";
import { useSpotifyAuth } from "./useSpotifyAuth";

interface TrackState {
  name: string;
  artist: string;
  albumArt: string;
  duration: number;
  position: number;
  paused: boolean;
}

interface UseSpotifyPlayerReturn {
  deviceId: string | null;
  track: TrackState | null;
  isReady: boolean;
  error: string | null;
  togglePlay: () => void;
  skipNext: () => void;
  skipPrev: () => void;
  setVolume: (volume: number) => void;
  disconnect: () => void;
}

export function useSpotifyPlayer(active: boolean): UseSpotifyPlayerReturn {
  const { getValidToken, isAuthenticated } = useSpotifyAuth();
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [track, setTrack] = useState<TrackState | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const playerRef = useRef<Spotify.Player | null>(null);
  const failureCountRef = useRef(0);
  const lastTrackIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active || !isAuthenticated) return;

    let cancelled = false;

    const initPlayer = async () => {
      try {
        await loadSpotifySDK();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load Spotify SDK. Check ad-blockers.",
        );
        return;
      }

      if (cancelled) return;

      // Pre-check for Premium before attempting DRM playback
      try {
        const token = await getValidToken();
        const profile = await getCurrentUserProfile(token);
        if (cancelled) return;
        if (profile.product !== "premium") {
          setError(
            "Spotify Premium is required for playback. " +
            "Your account is on the \"" + profile.product + "\" plan."
          );
          return;
        }
      } catch (err) {
        // Don't block on network failures — the SDK will catch it later
        console.warn("[Spotify] Premium pre-check failed:", err);
      }

      if (cancelled) return;

      const player = new Spotify.Player({
        name: "UwU Clock",
        getOAuthToken: async (cb) => {
          try {
            const token = await getValidToken();
            cb(token);
          } catch {
            setError("Failed to get valid token");
          }
        },
        volume: 0.5,
      });

      player.addListener("ready", ({ device_id }) => {
        if (cancelled) return;
        setDeviceId(device_id);
        setIsReady(true);
        setError(null);
      });

      player.addListener("not_ready", () => {
        if (cancelled) return;
        setIsReady(false);
        setDeviceId(null);
      });

      player.addListener("player_state_changed", (state) => {
        if (cancelled || !state) return;
        const currentTrack = state.track_window.current_track;

        // Detect rapid track cycling (DRM failure causes skip with position < 2s)
        if (!state.paused && currentTrack.id !== lastTrackIdRef.current) {
          if (state.position < 2000 && lastTrackIdRef.current !== null) {
            failureCountRef.current++;
            if (failureCountRef.current >= 3) {
              player.pause().catch(() => {});
              setError(
                "Playback failed — tracks are skipping rapidly. " +
                "This may be a browser DRM issue. " +
                "Try a different browser or disable extensions that block third-party cookies."
              );
              return;
            }
          } else {
            failureCountRef.current = 0;
          }
          lastTrackIdRef.current = currentTrack.id;
        }

        // Reset counter when a track plays successfully past 2s
        if (!state.paused && state.position >= 2000) {
          failureCountRef.current = 0;
        }

        setTrack({
          name: currentTrack.name,
          artist: currentTrack.artists.map((a) => a.name).join(", "),
          albumArt: currentTrack.album.images[0]?.url ?? "",
          duration: state.duration,
          position: state.position,
          paused: state.paused,
        });
      });

      player.addListener("initialization_error", ({ message }) => {
        if (cancelled) return;
        setError(`Initialization error: ${message}`);
      });

      player.addListener("authentication_error", ({ message }) => {
        if (cancelled) return;
        setError(`Authentication error: ${message}`);
      });

      player.addListener("account_error", () => {
        if (cancelled) return;
        setError("Spotify Premium is required for playback");
      });

      player.addListener("playback_error", ({ message }) => {
        if (cancelled) return;
        console.error("[Spotify] Playback error:", message);
        player.pause().catch(() => {});
        setError(
          "Playback failed — this may be a browser DRM issue. " +
          "Try a different browser or disable extensions that block third-party cookies."
        );
      });

      const connected = await player.connect();
      if (!connected && !cancelled) {
        setError("Failed to connect to Spotify");
      }

      playerRef.current = player;
    };

    void initPlayer();

    return () => {
      cancelled = true;
      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
      }
      failureCountRef.current = 0;
      lastTrackIdRef.current = null;
      setDeviceId(null);
      setIsReady(false);
      setTrack(null);
      setError(null);
    };
  }, [active, isAuthenticated, getValidToken]);

  const togglePlay = useCallback(() => {
    playerRef.current?.togglePlay();
  }, []);

  const skipNext = useCallback(() => {
    playerRef.current?.nextTrack();
  }, []);

  const skipPrev = useCallback(() => {
    playerRef.current?.previousTrack();
  }, []);

  const setVolumeFn = useCallback((volume: number) => {
    playerRef.current?.setVolume(volume / 100);
  }, []);

  const disconnect = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.disconnect();
      playerRef.current = null;
    }
    setDeviceId(null);
    setIsReady(false);
    setTrack(null);
  }, []);

  return {
    deviceId,
    track,
    isReady,
    error,
    togglePlay,
    skipNext,
    skipPrev,
    setVolume: setVolumeFn,
    disconnect,
  };
}
